import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import {
  useCloseTransactionMutation,
  useGetTransactionDetailQuery,
  useSetTransactionFinalStatusMutation,
} from "src/entities/transactions/api/api";
import { mapTransactionDetailDto } from "./mappers";
import { TransactionFinalStatus } from "./types";

type UseTransactionDetailOptions = {
  enabled?: boolean;
  pollingInterval?: number;
};

export function useTransactionDetail(
  transactionId: string | null,
  options?: UseTransactionDetailOptions,
) {
  const { enabled = true, pollingInterval = 0 } = options ?? {};

  const result = useGetTransactionDetailQuery(
    enabled && transactionId ? transactionId : skipToken,
    {
      pollingInterval,
      refetchOnMountOrArgChange: true,
    },
  );

  const data = useMemo(() => {
    if (!result.data) return null;

    return mapTransactionDetailDto(result.data.data);
  }, [result.data]);

  return {
    data,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    refetch: result.refetch,
  };
}

export function useTransactionMutations() {
  const [closeTransactionMutation] = useCloseTransactionMutation();
  const [setTransactionFinalStatusMutation] =
    useSetTransactionFinalStatusMutation();

  const closeTransaction = async (id: string) => {
    await closeTransactionMutation(id).unwrap();
  };

  const setFinalStatus = async (id: string, status: TransactionFinalStatus) => {
    await setTransactionFinalStatusMutation({
      transactionId: id,
      status,
    }).unwrap();
  };

  return {
    closeTransaction,
    setFinalStatus,
  };
}
